import { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import BookingLayout from 'pages/booking/BookingLayout';
import BookingNotFound from 'pages/booking/BookingNotFound';
import IconPencil from 'assets/pencil.svg';
import IconArrowLeft from 'assets/arrow-narrow-left.svg';
import { useApi } from 'api/api-context';
import { BOOKING_SLUG_THANKYOU_PATH } from 'routes/paths';
import useLang from 'lang';
import { formatLocaleDate, formatLocaleTime } from 'lang/date-time';
import 'pages/booking/BookingContact.css';

/** Returns the number of days until the given date string, or null if in the past. */
function daysUntil(dateStr?: string) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
}

/** Submits the reservation. */
export default function BookingContact() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { state } = useLocation();
  const { getAction, jwt } = useApi();
  const { _t, _tn, lang } = useLang();
  const initialBooking: any = state ?? JSON.parse(sessionStorage.getItem('bookingState') ?? 'null') ?? {};
  const [booking, setBooking] = useState<any>(initialBooking);

  const [gdpr, setGdpr] = useState(false);
  const [terms, setTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const isGuest = !jwt;
  const initialBilling = booking.service?.billingAddress ?? {};
  const [billingStreet, setBillingStreet] = useState(initialBilling.street ?? '');
  const [billingNumber, setBillingNumber] = useState(initialBilling.number ?? '');
  const [billingCity, setBillingCity] = useState(initialBilling.city ?? '');
  const [billingZip, setBillingZip] = useState(initialBilling.zip ?? '');
  const guestDetailsValid = !isGuest || Boolean(firstName.trim() && lastName.trim() && email.trim());
  const requiresBilling = booking.service?.paymentMode === 'buyable-credits';
  const requiresMonetaryPayment = requiresBilling && Number(booking.paidSlots) > 0;
  const billingValid =
    !requiresMonetaryPayment ||
    Boolean(billingStreet.trim() && billingNumber.trim() && billingCity.trim() && billingZip.trim());
  const formValid = gdpr && terms && guestDetailsValid && billingValid;
  const formattedBookingDate = booking.slot?.startsAt
    ? formatLocaleDate(booking.slot.startsAt, lang)
    : booking.dateRaw
      ? formatLocaleDate(booking.dateRaw, lang)
      : '';
  const formattedBookingTime =
    booking.slot?.startsAt && booking.slot?.endsAt
      ? `${formatLocaleTime(booking.slot.startsAt, lang)} – ${formatLocaleTime(booking.slot.endsAt, lang)}`
      : '';

  /** Submits the reservation and navigates to the thank-you page on success. */
  const handleSubmit = async () => {
    if (!formValid || submitting) return;
    setSubmitting(true);
    setApiError(null);
    try {
      const res = isGuest
        ? await getAction('CreateGuestReservation')({
            payload: {
              slug: slug || booking.service?.slug,
              startsAt: booking.slot?.startsAt,
              endsAt: booking.slot?.endsAt,
              personsCount: booking.persons,
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              email: email.trim(),
              phoneNumber: phoneNumber.trim() || undefined,
              language: lang as 'sk' | 'cs' | 'en',
              termsAccepted: true,
              privacyAccepted: true,
              billing: requiresMonetaryPayment
                ? {
                    street: billingStreet.trim(),
                    number: billingNumber.trim(),
                    city: billingCity.trim(),
                    zip: billingZip.trim(),
                  }
                : undefined,
            },
          })
        : await getAction('CreateReservation')({
            payload: {
              reservableSpaceId: booking.service?.id,
              startsAt: booking.slot?.startsAt,
              endsAt: booking.slot?.endsAt,
              personsCount: booking.persons,
              billing: requiresMonetaryPayment
                ? {
                    street: billingStreet.trim(),
                    number: billingNumber.trim(),
                    city: billingCity.trim(),
                    zip: billingZip.trim(),
                  }
                : undefined,
            },
          });
      const reservation = (res as any).reservation;
      const confirmation = {
        ...booking,
        service: {
          ...booking.service,
          creditBalance:
            reservation?.remainingCredits == null
              ? booking.service?.creditBalance
              : { ...booking.service?.creditBalance, available: reservation.remainingCredits },
        },
        reservation,
        payment: (res as any).payment,
      };
      sessionStorage.setItem('bookingConfirmation', JSON.stringify(confirmation));
      sessionStorage.removeItem('bookingState');
      sessionStorage.removeItem('bookingStripeReturned');
      if ((res as any).payment?.paymentAccessToken) {
        sessionStorage.setItem('bookingPaymentAccessToken', (res as any).payment.paymentAccessToken);
      }
      const checkoutUrl = (res as any).payment?.attempt?.checkoutUrl;
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
      navigate(BOOKING_SLUG_THANKYOU_PATH.insert({ slug: slug || booking.service?.slug }), { state: confirmation });
    } catch (err: any) {
      const error = err.response?.data;
      if (error?.code === 4912) {
        // The credit balance may have changed since the slot summary was calculated.
        // Use the server-provided settlement split and require billing before retrying.
        const nextBooking = {
          ...booking,
          creditCost: Number(error.creditsApplied),
          paidSlots: Number(error.paidSlots),
          projectedCredits: Math.max(
            0,
            Number(booking.service?.creditBalance?.available ?? 0) - Number(error.creditsApplied)
          ),
        };
        setBooking(nextBooking);
        sessionStorage.setItem('bookingState', JSON.stringify(nextBooking));
        setApiError(_t('Your credit balance changed. Add a billing address to continue.'));
      } else if (error?.code === 4911) {
        try {
          const refreshed: any = await getAction('GetReservableSpace')({
            parameters: { id: booking.service.id },
          });
          const nextBooking = {
            ...booking,
            service: { ...booking.service, creditBalance: refreshed.space?.creditBalance },
          };
          setBooking(nextBooking);
          sessionStorage.setItem('bookingState', JSON.stringify(nextBooking));
        } catch {
          // Preserve the server-provided counts when a follow-up refresh fails.
        }
        const requiredCredits = _tn('%d credit', '%d credits', Number(error.requiredCredits)).replace(
          '%d',
          String(error.requiredCredits)
        );
        setApiError(
          _t('This reservation requires %s. Available balance: %d.')
            .replace('%s', requiredCredits)
            .replace('%d', String(error.availableCredits))
        );
      } else if (error?.code === 4917) {
        setApiError(_t('Card payment is currently unavailable. Please try again later.'));
      } else {
        setApiError(error?.info || error?.msg || _t('Failed to create reservation. Please try again.'));
      }
      setSubmitting(false);
    }
  };

  const days = daysUntil(booking.dateRaw);

  if (isGuest && (booking.service?.paymentMode !== 'buyable-credits' || booking.service?.openForGuests !== true)) {
    return <BookingNotFound />;
  }

  return (
    <BookingLayout>
      <div className="contact-page">
        <div className="contact-inner">
          <button className="back-btn" onClick={() => navigate(-1)}>
            <img src={IconArrowLeft} width={20} height={20} alt="" />
            {_t('Back to slot selection')}
          </button>

          <div className="contact-summary-centered">
            <div className="summary-card">
              <h2 className="summary-title">{_t('Booking summary')}</h2>
              {booking.service && (
                <>
                  <p className="summary-service-name">{booking.service.title}</p>
                  <div className="summary-rows">
                    <div className="summary-row">
                      <div className="summary-row-left">
                        <span className="summary-row-label">{_t('Date')}</span>
                        <span className="summary-row-value">{formattedBookingDate}</span>
                      </div>
                      <button className="summary-edit-btn" onClick={() => navigate(-1)} title={_t('Change date')}>
                        <img src={IconPencil} width={22} height={22} alt="" />
                      </button>
                    </div>
                    <div className="summary-row">
                      <div className="summary-row-left">
                        <span className="summary-row-label">{_t('Time')}</span>
                        <span className="summary-row-value">{formattedBookingTime}</span>
                      </div>
                      <button className="summary-edit-btn" onClick={() => navigate(-1)} title={_t('Change time')}>
                        <img src={IconPencil} width={22} height={22} alt="" />
                      </button>
                    </div>
                    {booking.persons && (
                      <div className="summary-row">
                        <div className="summary-row-left">
                          <span className="summary-row-label">{_t('Persons')}</span>
                          <span className="summary-row-value">{booking.persons}</span>
                        </div>
                      </div>
                    )}
                    {!isGuest && ['credit', 'buyable-credits'].includes(booking.service?.paymentMode) && (
                      <>
                        <div className="summary-row">
                          <div className="summary-row-left">
                            <span className="summary-row-label">{_t('Credit cost')}</span>
                            <span className="summary-row-value">{booking.creditCost}</span>
                          </div>
                        </div>
                        <div className="summary-row">
                          <div className="summary-row-left">
                            <span className="summary-row-label">{_t('Credits after reservation')}</span>
                            <span className="summary-row-value">{booking.projectedCredits}</span>
                          </div>
                        </div>
                      </>
                    )}
                    {booking.service?.paymentMode === 'buyable-credits' && (
                      <>
                        <div className="summary-row">
                          <div className="summary-row-left">
                            <span className="summary-row-label">{_t('Paid slots')}</span>
                            <span className="summary-row-value">{booking.paidSlots}</span>
                          </div>
                        </div>
                        <div className="summary-row">
                          <div className="summary-row-left">
                            <span className="summary-row-label">{_t('Price including VAT')}</span>
                            <span className="summary-row-value">
                              {Number(booking.grossPrice).toFixed(2)} {booking.service.currencyCode ?? 'EUR'}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="summary-consents">
                    {isGuest && (
                      <div className="contact-form">
                        <div className="form-row">
                          <label className="form-group">
                            <span className="form-label">{_t('First name')}</span>
                            <input
                              className="form-input"
                              value={firstName}
                              onChange={(event) => setFirstName(event.target.value)}
                              required
                            />
                          </label>
                          <label className="form-group">
                            <span className="form-label">{_t('Last name')}</span>
                            <input
                              className="form-input"
                              value={lastName}
                              onChange={(event) => setLastName(event.target.value)}
                              required
                            />
                          </label>
                        </div>
                        <label className="form-group">
                          <span className="form-label">{_t('Email')}</span>
                          <input
                            className="form-input"
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            required
                          />
                        </label>
                        <label className="form-group">
                          <span className="form-label">{_t('Phone number (optional)')}</span>
                          <input
                            className="form-input"
                            type="tel"
                            value={phoneNumber}
                            onChange={(event) => setPhoneNumber(event.target.value)}
                          />
                        </label>
                      </div>
                    )}
                    {requiresMonetaryPayment && (
                      <div className="contact-form">
                        <h3>{_t('Billing address')}</h3>
                        <div className="form-row">
                          <label className="form-group">
                            <span className="form-label">{_t('Street')}</span>
                            <input
                              className="form-input"
                              value={billingStreet}
                              onChange={(event) => setBillingStreet(event.target.value)}
                              required
                            />
                          </label>
                          <label className="form-group">
                            <span className="form-label">{_t('House number')}</span>
                            <input
                              className="form-input"
                              value={billingNumber}
                              onChange={(event) => setBillingNumber(event.target.value)}
                              required
                            />
                          </label>
                        </div>
                        <div className="form-row">
                          <label className="form-group">
                            <span className="form-label">{_t('City')}</span>
                            <input
                              className="form-input"
                              value={billingCity}
                              onChange={(event) => setBillingCity(event.target.value)}
                              required
                            />
                          </label>
                          <label className="form-group">
                            <span className="form-label">{_t('ZIP code')}</span>
                            <input
                              className="form-input"
                              value={billingZip}
                              onChange={(event) => setBillingZip(event.target.value)}
                              required
                            />
                          </label>
                        </div>
                      </div>
                    )}
                    <label className="checkbox-label">
                      <input type="checkbox" checked={gdpr} onChange={(e) => setGdpr(e.target.checked)} />
                      <span>
                        {_t('I agree to the processing of personal data according to the')}{' '}
                        <a href="https://admin.phillip.city/privacy-policy/" className="checkbox-link">
                          {_t('Privacy Policy')}
                        </a>
                        .
                      </span>
                    </label>
                    <label className="checkbox-label">
                      <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
                      <span>
                        {_t('I agree with the')}{' '}
                        <a href="https://admin.phillip.city/privacy-policy/" className="checkbox-link">
                          {_t('General Terms and Conditions')}
                        </a>
                      </span>
                    </label>
                  </div>
                </>
              )}
            </div>

            {booking.service && (
              <div className="summary-actions">
                {days && (
                  <p className="summary-days">
                    {_tn('Reservation in %d day', 'Reservation in %d days', days).replace('%d', String(days))}
                  </p>
                )}
                {apiError && <p className="summary-error">{apiError}</p>}
                <button className="cta-btn" onClick={handleSubmit} disabled={!formValid || submitting}>
                  {submitting
                    ? _t('Processing...')
                    : requiresMonetaryPayment
                      ? _t('Reserve with obligation to pay')
                      : booking.service?.paymentMode === 'free' && booking.service.autoAcceptReservations === false
                        ? _t('Request reservation')
                        : _t('Create reservation')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </BookingLayout>
  );
}
